import React from "react";
import { Link } from "@/lib/router-compat";
import {
  ArrowRight,
  Download,
  Printer,
  Award,
  Clock,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const COMPARISON_IMAGE_URL =
  "https://vibe.filesafe.space/1787327131284421342/attachments/62b4a6ae-016a-47f4-af4a-f13d423d5dfd.png";

export const SaintComparisonCallout: React.FC = () => {
  const handlePrint = (e: React.MouseEvent) => {
    e.preventDefault();
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      window.print();
      return;
    }
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>How SAINT Compares to Other Depression Treatment Options - Neuro Wellness</title>
          <style>
            body { margin: 0; padding: 20px; font-family: sans-serif; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #fff; }
            img { max-width: 100%; height: auto; max-height: 95vh; object-fit: contain; }
            @media print {
              body { padding: 0; }
              img { width: 100%; max-height: 100vh; }
            }
          </style>
        </head>
        <body>
          <img src="${COMPARISON_IMAGE_URL}" alt="How SAINT Compares to Other Depression Treatment Options" onload="window.print(); window.close();" />
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <section
      aria-labelledby="saint-comparison-heading"
      className="py-16 sm:py-20 bg-muted/40 border-y border-border"
    >
      <div className="container-px mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Top Header */}
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs sm:text-sm font-bold uppercase tracking-wider mb-3">
            <Award className="h-4 w-4 text-accent" />
            Side-by-Side Comparison
          </div>
          <h2
            id="saint-comparison-heading"
            className="font-headings text-2xl sm:text-3xl lg:text-4xl font-extrabold text-primary tracking-tight"
          >
            How SAINT® Compares to Other Depression Treatments
          </h2>
          <p className="mt-3 text-base sm:text-lg text-muted-foreground leading-relaxed">
            When you've tried two or more medications without enough relief, the
            two numbers that matter most are{" "}
            <strong className="text-primary">remission rate</strong> and{" "}
            <strong className="text-primary">average time to remission</strong>.
          </p>
        </div>

        {/* Content Card with Split Layout: Table / Text on Left, Downloadable Graphic Card on Right */}
        <div className="bg-card rounded-2xl border border-border shadow-md overflow-hidden grid lg:grid-cols-12 gap-0">
          {/* Left Column: Metrics Table & Key Highlights */}
          <div className="lg:col-span-7 p-6 sm:p-8 lg:p-10 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between pb-4 border-b border-border mb-5">
                <span className="text-xs uppercase font-bold tracking-wider text-muted-foreground">
                  Published Clinical Studies
                </span>
                <span className="text-xs font-semibold text-primary flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5 text-accent" /> 5-Day Protocol
                </span>
              </div>

              {/* Desktop table (md and up) */}
              <div className="hidden md:block overflow-x-auto -mx-2 sm:mx-0 mb-6">
                <table className="w-full text-xs sm:text-sm text-left border-collapse rounded-lg overflow-hidden border border-border">
                  <thead>
                    <tr className="bg-primary text-white">
                      <th className="py-2.5 px-3 font-semibold text-[11px] sm:text-xs">
                        Treatment
                      </th>
                      <th className="py-2.5 px-3 font-bold text-center bg-accent text-accent-foreground">
                        SAINT®
                      </th>
                      <th className="py-2.5 px-3 font-medium text-center">
                        ECT
                      </th>
                      <th className="py-2.5 px-3 font-medium text-center">
                        TMS
                      </th>
                      <th className="py-2.5 px-3 font-medium text-center">
                        Medication
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-background">
                    <tr>
                      <td className="py-3 px-3 font-bold text-primary">
                        Remission rate
                      </td>
                      <td className="py-3 px-3 text-center font-extrabold text-primary bg-accent/10">
                        79–90%
                      </td>
                      <td className="py-3 px-3 text-center text-foreground font-semibold">
                        48%
                      </td>
                      <td className="py-3 px-3 text-center text-foreground">
                        17–32%
                      </td>
                      <td className="py-3 px-3 text-center text-foreground">
                        14%
                      </td>
                    </tr>
                    <tr>
                      <td className="py-3 px-3 font-bold text-primary">
                        Time to remission
                      </td>
                      <td className="py-3 px-3 text-center font-extrabold text-primary bg-accent/10">
                        2.6 days
                      </td>
                      <td className="py-3 px-3 text-center text-foreground">
                        3 weeks
                      </td>
                      <td className="py-3 px-3 text-center text-foreground">
                        3.5 weeks
                      </td>
                      <td className="py-3 px-3 text-center text-foreground">
                        6–12 weeks
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Mobile card layout (below md) */}
              <div className="md:hidden mb-6 space-y-3">
                {[
                  {
                    metric: "Remission rate",
                    saint: "79–90%",
                    ect: "48%",
                    tms: "17–32%",
                    med: "14%",
                  },
                  {
                    metric: "Time to remission",
                    saint: "2.6 days",
                    ect: "3 weeks",
                    tms: "3.5 weeks",
                    med: "6–12 weeks",
                  },
                ].map((row) => (
                  <div
                    key={row.metric}
                    className="rounded-lg border border-border bg-background overflow-hidden"
                  >
                    <div className="bg-primary px-3 py-2">
                      <p className="text-xs font-bold text-white">
                        {row.metric}
                      </p>
                    </div>
                    <div className="divide-y divide-border">
                      {[
                        { label: "SAINT®", val: row.saint, highlight: true },
                        { label: "ECT", val: row.ect, highlight: false },
                        { label: "TMS", val: row.tms, highlight: false },
                        { label: "Medication", val: row.med, highlight: false },
                      ].map((item) => (
                        <div
                          key={item.label}
                          className={`flex items-center justify-between px-3 py-2 ${
                            item.highlight ? "bg-accent/10" : ""
                          }`}
                        >
                          <span
                            className={`text-xs font-semibold ${
                              item.highlight
                                ? "text-primary"
                                : "text-muted-foreground"
                            }`}
                          >
                            {item.label}
                          </span>
                          <span
                            className={`text-sm font-bold ${
                              item.highlight
                                ? "text-primary"
                                : "text-foreground"
                            }`}
                          >
                            {item.val}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-2 text-xs sm:text-sm text-muted-foreground leading-relaxed mb-6">
                <p>
                  <strong className="text-primary">
                    Why SAINT's numbers differ:
                  </strong>{" "}
                  Personalized resting-state fMRI targeting maps your individual
                  brain connectivity, and 10 daily sessions compress what used
                  to take 6–9 weeks into just 5 consecutive days.
                </p>
                <p className="text-xs text-muted-foreground italic">
                  Data drawn from published trials in treatment-resistant
                  depression. Individual results vary.
                </p>
              </div>
            </div>

            {/* Read Article CTA */}
            <div className="pt-4 border-t border-border flex flex-wrap items-center gap-3">
              <Button
                asChild
                className="bg-primary text-white hover:bg-primary/90 font-semibold text-xs sm:text-sm shadow-sm"
              >
                <Link to="/how-saint-compares-to-other-depression-treatment-options">
                  <span>Read Full Comparison Article</span>
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="text-xs sm:text-sm font-semibold border-primary/30 text-primary hover:bg-primary/5"
              >
                <Link to="/saint">Explore SAINT®</Link>
              </Button>
            </div>
          </div>

          {/* Right Column: Downloadable / Printable Flyer Preview */}
          <div className="lg:col-span-5 bg-gradient-to-br from-primary/10 via-accent/10 to-primary/5 p-6 sm:p-8 flex flex-col items-center justify-center text-center border-t lg:border-t-0 lg:border-l border-border">
            <div className="relative group max-w-[240px] sm:max-w-[270px] mb-4">
              <a
                href={COMPARISON_IMAGE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block overflow-hidden rounded-xl shadow-lg border border-white/40 transition-transform group-hover:scale-102 duration-300"
                aria-label="View larger comparison chart image"
              >
                <img
                  src={COMPARISON_IMAGE_URL}
                  alt="How SAINT Compares to Other Depression Treatment Options"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                  width={480}
                  height={540}
                />
                <div className="absolute inset-0 bg-primary/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-bold uppercase tracking-wider rounded-xl">
                  Click to Enlarge
                </div>
              </a>
            </div>

            <h3 className="font-headings text-base sm:text-lg font-bold text-primary mb-1">
              SAINT® Comparison Handout
            </h3>
            <p className="text-xs text-muted-foreground mb-4 max-w-xs">
              Save or print this full side-by-side handout to discuss with your
              family or bring to your next medical appointment.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-2 w-full max-w-xs">
              <Button
                asChild
                size="sm"
                className="bg-primary text-white font-semibold flex-1 shadow-sm hover:opacity-90 text-xs"
              >
                <a
                  href={COMPARISON_IMAGE_URL}
                  download="SAINT-Depression-Treatment-Comparison.png"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" />
                  Download
                </a>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                className="border-primary/30 text-primary hover:bg-primary/5 font-semibold text-xs flex-1"
              >
                <Printer className="h-3.5 w-3.5 mr-1.5" />
                Print
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SaintComparisonCallout;
